/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { SwaggerStateService, SwaggerDomService, ApimInterceptorService } from './index';
import { SwaggerCssLoader } from './utils/swagger-css-loader';

declare const SwaggerUIBundle: any;

/**
 * Swagger UI Service
 *
 * Responsible for initializing and managing Swagger UI instances.
 * Delegates DOM operations to SwaggerDomService and state management to SwaggerStateService.
 */
@Injectable({
  providedIn: 'root'
})
export class SwaggerUIService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly TIMINGS = {
    POST_INIT_DELAY: 1000
  } as const;

  constructor(
    private readonly authService: AuthService,
    private readonly swaggerStateService: SwaggerStateService,
    private readonly swaggerDomService: SwaggerDomService,
    private readonly apimInterceptorService: ApimInterceptorService
  ) { }

  /**
   * Initializes Swagger UI in the provided DOM node
   * @param domNode Container element for Swagger UI
   * @param spec OpenAPI specification object
   * @returns Promise resolving to Swagger UI instance
   */
  async initializeSwaggerUI(domNode: Element, spec: any): Promise<any> {
    try {
      // Validate SwaggerUIBundle availability
      this.validateSwaggerUIBundle();

      // Validate spec
      this.validateSpec(spec);

      // Load Swagger UI CSS
      await SwaggerCssLoader.load();

      // Get access token
      const accessToken = await this.getAccessToken();

      // Create Swagger UI configuration with APIM interceptors
      const swaggerUIConfig = this.apimInterceptorService.createSwaggerUIConfig();

      // Set up auth context and swagger spec via state service
      swaggerUIConfig.setAuthContext(accessToken);
      swaggerUIConfig.setSwaggerSpec(spec);

      // Get OAuth configuration from state service
      const oauthConfig = this.swaggerStateService.getOAuthConfig();

      // Initialize Swagger UI Bundle
      const ui = SwaggerUIBundle({
        spec: spec,
        domNode: domNode,
        requestInterceptor: swaggerUIConfig.requestInterceptor,
        responseInterceptor: swaggerUIConfig.responseInterceptor,
        onComplete: swaggerUIConfig.onComplete,
        showMutatedRequest: false,
        oauth2RedirectUrl: window.location.origin + '/oauth2-redirect.html',
        usePkceWithAuthorizationCodeGrant: true
      });

      // Initialize OAuth if clientId is available
      this.initializeOAuth(ui, oauthConfig);

      // Set up post-initialization customizations
      this.setupPostInitialization(domNode);

      console.log('✅ Swagger UI initialized successfully');
      return ui;
    } catch (error) {
      console.error('❌ Error initializing Swagger UI:', error);
      throw error;
    }
  }

  /**
   * Clears Swagger UI content from DOM node
   * @param domNode Container element to clear
   */
  clearSwaggerUI(domNode: Element): void {
    if (domNode) {
      domNode.innerHTML = '';
    }
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Validates that SwaggerUIBundle is available
   */
  private validateSwaggerUIBundle(): void {
    if (typeof SwaggerUIBundle === 'undefined') {
      throw new Error(
        'SwaggerUIBundle is not loaded. Please check if swagger-ui-bundle.js is included in angular.json scripts array.'
      );
    }
  }

  /**
   * Validates OpenAPI specification
   */
  private validateSpec(spec: any): void {
    if (!spec || typeof spec !== 'object') {
      throw new Error('Invalid OpenAPI specification provided');
    }
  }

  /**
   * Gets access token from auth service
   */
  private async getAccessToken(): Promise<string> {
    try {
      return await firstValueFrom(this.authService.getAccessToken());
    } catch (error) {
      console.warn('⚠️ Failed to get access token:', error);
      return '';
    }
  }

  /**
   * Initializes OAuth configuration if clientId is available
   */
  private initializeOAuth(ui: any, oauthConfig: any): void {
    if (oauthConfig.clientId) {
      ui.initOAuth({
        clientId: oauthConfig.clientId,
        usePkceWithAuthorizationCodeGrant: true,
        scopes: oauthConfig.scopes
      });
    } else {
      ui.initOAuth({
        usePkceWithAuthorizationCodeGrant: true
      });
      console.warn('⚠️ No clientId found in spec, OAuth not initialized');
    }
  }

  /**
   * Sets up post-initialization customizations
   * Uses RxJS timer for proper cleanup support
   */
  private setupPostInitialization(domNode: Element): void {
    const baseUrl = this.swaggerStateService.getBaseServerUrl();

    // Inject data-url attributes
    if (baseUrl) {
      this.swaggerDomService.injectDataUrlAttributes(domNode, baseUrl)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }

    // Setup server selection handler
    this.swaggerDomService.setupServerSelectionHandler(
      domNode,
      (newServerUrl) => {
        this.swaggerDomService.updateDataUrlAttributesForServer(domNode, newServerUrl);
      }
    ).pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
